import {
  CardStyleInterpolators,
  StackScreenProps,
} from "@react-navigation/stack";
import {
  AccountFeatureFlag,
  SuggestType,
} from "@towersystems/roam-common/lib/generated-types";
import { Colors, Spacings } from "react-native-ui-lib";
import { ModalCloseButton } from "@towersystems/roam-common-native";
import { CustomerRootParamList } from "../../../../types";
import { useFullTextSearch } from "../../../../utilities/fulltext-search";
import { FulltextCustomerSearchContext } from "../../../../utilities/fulltext-search/context";
import { createStackHeaderNavigator } from "@towersystems/roam-common-native";
import { CreateCustomerScreen } from "../CreateCustomerScreen";
import { CustomerListScreen } from "../CustomerListScreen";
import { EditCustomerScreen } from "../EditCustomerScreen";
import { SaleScreen } from "../../../sales/screens/SaleScreen";
import { ParamListBase } from "@react-navigation/native";
import { useCustomerRootScreenActionsContextProvider } from "./hooks";
import { CustomerListFilterRootScreen } from "../../../customer/screens";
import { ListFilterProvider } from "../../../../utilities/list-filter/use-list-filter-context";
import { defaultCustomerFilterValues } from "../../../customer";
import { CustomerFilterValues } from "../../../customer/types";
import { useListFilter } from "../../../../utilities/list-filter";
import { vendorCanAccessFeature } from "../../../../utilities/feature-flags";
import { useCurrentVendorProvider } from "../../../../utilities/vendor";

export type CustomerRootScreenRendererProps = StackScreenProps<
  ParamListBase,
  "CustomerRootScreen"
>;

export const CustomerRootScreen = (props: CustomerRootScreenRendererProps) => {
  const maybeContext = useCustomerRootScreenActionsContextProvider();
  const { currentVendor } = useCurrentVendorProvider();

  const Stack = createStackHeaderNavigator<CustomerRootParamList>();

  const customerFullTextSearch = useFullTextSearch({
    type: SuggestType.CUSTOMER,
    responseType: "complete",
  });

  const filterContext = useListFilter<CustomerFilterValues>({
    defaultValues: defaultCustomerFilterValues,
  });

  return (
    <ListFilterProvider {...filterContext}>
      <FulltextCustomerSearchContext.Provider value={customerFullTextSearch}>
        <Stack.Navigator
          screenOptions={{
            cardStyle: {
              backgroundColor: Colors.surface,
              height: "100%",
            },
            headerShown: true,
            animationEnabled: true,
            headerTitleAlign: "center",
            headerLeftContainerStyle: { paddingLeft: Spacings.s5 },
            headerTintColor: Colors.text,
            headerRightContainerStyle: { paddingRight: Spacings.s5 },
            cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
            headerStyle: {
              backgroundColor: Colors.surface,
              borderBottomWidth: 1,
              borderBottomColor: Colors.borderColor,
            },
            headerLeft: () => <ModalCloseButton />,
          }}
        >
          <Stack.Screen
            name="CustomerListScreen"
            component={CustomerListScreen}
            options={{
              title: maybeContext?.customerListScreenTitle
                ? maybeContext?.customerListScreenTitle
                : "Select Customer",
            }}
          />
          <Stack.Group
            screenOptions={{
              headerShown: true,
              headerLeft: () => <ModalCloseButton type="Back" />,
              title: "",
            }}
          >
            {vendorCanAccessFeature(
              AccountFeatureFlag.POS_APP_MANAGE_CUSTOMERS_UI,
              currentVendor
            ) && (
              <Stack.Screen
                name="CreateCustomerScreen"
                component={CreateCustomerScreen}
              />
            )}
            {vendorCanAccessFeature(
              AccountFeatureFlag.POS_APP_MANAGE_CUSTOMERS_UI,
              currentVendor
            ) && (
              <Stack.Screen
                name="EditCustomerScreen"
                component={EditCustomerScreen}
              />
            )}
            <Stack.Screen
              name="SaleScreen"
              component={SaleScreen}
              options={{ headerTitle: "", headerLeft: () => <ModalCloseButton type="Back" /> }}
            />
          </Stack.Group>
          <Stack.Group
            screenOptions={{
              headerShown: false,
              presentation: "modal",
            }}
          >
            <Stack.Screen
              name="CustomerListFilterRootScreen"
              component={CustomerListFilterRootScreen}
            />
          </Stack.Group>
        </Stack.Navigator>
      </FulltextCustomerSearchContext.Provider>
    </ListFilterProvider>
  );
};
